import { useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Stack,
    TextField,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { Icon } from '../../shared/Icon';

import {
    JiraIssueSummary,
    useCreateJiraIssue,
    useJiraConfig,
} from '../../hooks/useJiraApi';

export const PlanNewGiftButton = () => {
    const [open, setOpen] = useState(false);
    const [summary, setSummary] = useState('');
    const [description, setDescription] = useState('');
    const [result, setResult] = useState<{ key: string; url: string } | null>(
        null,
    );
    const [error, setError] = useState<string | null>(null);

    const jiraConfigQuery = useJiraConfig();
    const createJiraIssue = useCreateJiraIssue();
    const queryClient = useQueryClient();

    const reset = () => {
        setOpen(false);
        setSummary('');
        setDescription('');
        setResult(null);
        setError(null);
    };

    const handleCreate = async () => {
        setError(null);
        try {
            const issue = await createJiraIssue.mutateAsync({
                body: {
                    summary: summary.trim(),
                    description: description.trim() || undefined,
                },
            });
            setResult({ key: issue.key, url: issue.url });

            // Jira's JQL index has a few-second lag before a freshly created
            // issue shows up in search results. Instead of forcing the user
            // to refresh, prepend the issue to every currently cached search
            // page ourselves — the shape returned by createIssue matches
            // what the search endpoint returns.
            queryClient.setQueriesData<JiraIssueSummary[]>(
                { queryKey: ['get', '/api/gift-jira/v1/issues'] },
                (existing) => (existing ? [issue, ...existing] : [issue]),
            );
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to create Jira story',
            );
        }
    };

    // Hide the button entirely if Jira is not enabled
    if (!jiraConfigQuery.data?.enabled) {
        return null;
    }

    return (
        <>
            <Dialog open={open} onClose={reset}>
                <DialogTitle>Plan new gift</DialogTitle>
                <DialogContent>
                    {result ? (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            Created{' '}
                            <Link
                                href={result.url}
                                target="_blank"
                                rel="noreferrer"
                                underline="hover"
                                sx={{ fontWeight: 600 }}
                            >
                                {result.key}
                            </Link>
                        </Alert>
                    ) : (
                        <Stack spacing={2} sx={{ mt: 1, minWidth: 400 }}>
                            <TextField
                                label="Summary"
                                size="small"
                                value={summary}
                                onChange={(e) => setSummary(e.target.value)}
                                fullWidth
                                autoFocus
                                required
                            />
                            <TextField
                                label="Description"
                                size="small"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                multiline
                                minRows={3}
                                fullWidth
                                placeholder="Add context for the team (optional)…"
                            />
                            {error && (
                                <Alert
                                    severity="error"
                                    onClose={() => setError(null)}
                                >
                                    {error}
                                </Alert>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    {result ? (
                        <Button onClick={reset}>Done</Button>
                    ) : (
                        <>
                            <Button onClick={reset}>Cancel</Button>
                            <Button
                                variant="contained"
                                disabled={
                                    !summary.trim() || createJiraIssue.isPending
                                }
                                onClick={handleCreate}
                            >
                                {createJiraIssue.isPending
                                    ? 'Creating…'
                                    : 'Create Story'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <Button
                startIcon={<Icon name="add" />}
                onClick={() => setOpen(true)}
                variant="contained"
            >
                Plan new gift
            </Button>
        </>
    );
};
